import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { crmBase, parseCrmBranchIdFromPath } from '@crm/crmPaths';
import { logoutManager } from '@crm/features/auth/crmAuthSlice';
import { hasCrmPermission } from '@crm/permissions';
import { SelectPlain } from '@shared/common/select/SelectPlain';
import { ThemeToggle } from '@shared/components/ThemeToggle';

const MENU = [
  { to: '', label: 'Обзор', end: true, permissionKey: 'menu.dashboard' },
  { to: 'clients', label: 'Клиенты', end: true, permissionKey: 'menu.clients' },
  { to: 'funnel', label: 'Воронка', permissionKey: 'menu.funnel' },
  { to: 'sale', label: 'Продажи', permissionKey: 'menu.sale' },
  { to: 'expenditures', label: 'Расходы', permissionKey: 'menu.expenditures' },
  { to: 'staffs', label: 'Сотрудники', permissionKey: 'menu.staffs' },
  { to: 'directions', label: 'Направления', permissionKey: 'menu.directions' },
  { to: 'goods', label: 'Товары', permissionKey: 'menu.goods' },
  { to: 'calendar', label: 'Календарь', permissionKey: 'menu.calendar' },
  { to: 'events', label: 'События', permissionKey: 'menu.events' },
];

export function CrmBranchHeader() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ownerId } = useParams();
  const { pathname } = useLocation();
  const branchId = parseCrmBranchIdFromPath(pathname, ownerId);
  const { branches, profile } = useSelector((s) => s.crmAuth);
  const isOwner = Boolean(profile?.isOwner || profile?.is_owner);
  const canOpenSettings = hasCrmPermission(profile, branches, branchId, 'settings.view');
  const [burgerOpen, setBurgerOpen] = useState(false);

  const base = ownerId && branchId ? `/${ownerId}/crm/${branchId}` : '';

  useEffect(() => {
    setBurgerOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!burgerOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setBurgerOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [burgerOpen]);

  const onLogout = async () => {
    setBurgerOpen(false);
    await dispatch(logoutManager());
    navigate(ownerId ? crmBase(ownerId) : '/');
  };

  const onBranchChange = useCallback(
    (nextId) => {
      if (!nextId || nextId === branchId || !ownerId) return;
      navigate(`/${ownerId}/crm/${nextId}`, { replace: true });
    },
    [branchId, navigate, ownerId],
  );

  const branchOptions = useMemo(
    () => branches.map((b) => ({ value: b.id, title: b.name })),
    [branches],
  );

  const activeBranch = branches.find((b) => b.id === branchId);
  const visibleMenu = MENU.filter((m) =>
    hasCrmPermission(profile, branches, branchId, m.permissionKey),
  );

  if (!ownerId || !branchId || !base) {
    return null;
  }

  return (
    <>
      <nav className="crm-header__nav" aria-label="Разделы CRM">
        {visibleMenu.map((m) => (
          <NavLink key={m.to || 'root'} to={m.to ? `${base}/${m.to}` : base} end={m.end}>
            {m.label}
          </NavLink>
        ))}
      </nav>
      <div className="crm-header__actions">
        {branches.length > 1 ? (
          <SelectPlain
            className="crm-header__branch-select-wrap"
            id="crm-header-branch"
            aria-label="Филиал"
            value={branchId ?? ''}
            onChange={onBranchChange}
            options={branchOptions}
            size="default"
          />
        ) : (
          <span className="crm-header__branch-static">{activeBranch?.name ?? '—'}</span>
        )}
        <ThemeToggle />
        <button
          type="button"
          className="crm-header__burger"
          aria-label="Меню"
          aria-expanded={burgerOpen}
          onClick={() => setBurgerOpen(true)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>
      {burgerOpen ? (
        <div
          className="mobile-menu-backdrop"
          role="presentation"
          onClick={() => setBurgerOpen(false)}
        >
          <aside
            className="mobile-menu"
            aria-label="Меню CRM"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mobile-menu__header">
              <div className="mobile-menu__title">Меню</div>
              <button
                type="button"
                className="mobile-menu__close"
                aria-label="Закрыть меню"
                onClick={() => setBurgerOpen(false)}
              >
                ×
              </button>
            </div>
            <nav className="mobile-menu__nav crm-header__mobile-nav" aria-label="Разделы CRM">
              {visibleMenu.map((m) => (
                <NavLink
                  key={m.to || 'root'}
                  to={m.to ? `${base}/${m.to}` : base}
                  end={m.end}
                  onClick={() => setBurgerOpen(false)}
                >
                  {m.label}
                </NavLink>
              ))}
            </nav>
            {(isOwner || canOpenSettings) && (
              <nav className="mobile-menu__nav" aria-label="Аккаунт">
                {isOwner && (
                  <NavLink to="/profile" onClick={() => setBurgerOpen(false)}>
                    Личный кабинет
                  </NavLink>
                )}
                {canOpenSettings && (
                  <NavLink to={`${base}/settings`} onClick={() => setBurgerOpen(false)}>
                    Настройки
                  </NavLink>
                )}
              </nav>
            )}
            <button className="mobile-menu__logout" type="button" onClick={onLogout}>
              Выйти
            </button>
          </aside>
        </div>
      ) : null}
    </>
  );
}
